import { useState } from 'react';
import { X, Sparkles, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import { useCreateRule } from '@/hooks/useRules';

export default function FilterCreationDialog({ analysisData, isOpen, onClose }) {
  const [selectedRuleIndex, setSelectedRuleIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    patterns: true,
    semantic: true,
    rules: true,
  });

  const createRuleMutation = useCreateRule();

  if (!isOpen || !analysisData) return null;

  const { email_count, patterns, semantic_analysis, suggested_rules } = analysisData;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCreateRule = async () => {
    const selectedRule = suggested_rules[selectedRuleIndex];
    if (!selectedRule) return;

    try {
      await createRuleMutation.mutateAsync({
        name: selectedRule.name,
        description: selectedRule.description,
        conditions: selectedRule.conditions,
        match_type: selectedRule.match_type || 'all',
        actions: selectedRule.actions,
        enabled: true,
      });
      onClose();
    } catch (error) {
      console.error('Failed to create rule:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold">Create Filter from Emails</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Analyzed {email_count} email{email_count !== 1 ? 's' : ''} to suggest filter rules
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Common Patterns */}
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('patterns')}
              className="w-full flex items-center justify-between p-4 hover:bg-accent transition-colors"
            >
              <h3 className="font-semibold text-lg">Common Patterns</h3>
              {expandedSections.patterns ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </button>

            {expandedSections.patterns && (
              <div className="p-4 border-t space-y-4">
                {/* Domains */}
                {patterns.domains && patterns.domains.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Top Sender Domains</h4>
                    <div className="space-y-1">
                      {patterns.domains.slice(0, 5).map((domain) => (
                        <div
                          key={domain.domain}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="font-mono text-muted-foreground">
                            {domain.domain}
                          </span>
                          <Badge variant="secondary">
                            {domain.count} ({Math.round(domain.percentage)}%)
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Keywords */}
                {patterns.common_keywords && patterns.common_keywords.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Common Subject Keywords</h4>
                    <div className="flex flex-wrap gap-2">
                      {patterns.common_keywords.map((keyword) => (
                        <Badge key={keyword} variant="outline">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Categories */}
                {patterns.categories && patterns.categories.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Detected Categories</h4>
                    <div className="space-y-1">
                      {patterns.categories.map((cat) => (
                        <div
                          key={cat.category}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="capitalize">{cat.category}</span>
                          <Badge variant="secondary">
                            {cat.count} ({Math.round(cat.percentage)}%)
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* AI Semantic Analysis */}
          {semantic_analysis && (
            <div className="border rounded-lg bg-blue-50 dark:bg-blue-950/20">
              <button
                onClick={() => toggleSection('semantic')}
                className="w-full flex items-center justify-between p-4 hover:bg-accent transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <h3 className="font-semibold text-lg">AI Analysis</h3>
                </div>
                {expandedSections.semantic ? (
                  <ChevronUp className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </button>

              {expandedSections.semantic && (
                <div className="p-4 border-t space-y-3">
                  {semantic_analysis.semantic_theme && (
                    <div>
                      <h4 className="text-sm font-medium mb-1">Theme</h4>
                      <p className="text-sm text-muted-foreground">
                        {semantic_analysis.semantic_theme}
                      </p>
                    </div>
                  )}

                  {semantic_analysis.sender_type && (
                    <div>
                      <h4 className="text-sm font-medium mb-1">Sender Type</h4>
                      <p className="text-sm text-muted-foreground">
                        {semantic_analysis.sender_type}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-4">
                    {semantic_analysis.suggested_category && (
                      <div>
                        <h4 className="text-sm font-medium mb-1">Suggested Category</h4>
                        <Badge className="capitalize">
                          {semantic_analysis.suggested_category}
                        </Badge>
                      </div>
                    )}

                    {semantic_analysis.suggested_priority && (
                      <div>
                        <h4 className="text-sm font-medium mb-1">Suggested Priority</h4>
                        <Badge className="capitalize">
                          {semantic_analysis.suggested_priority}
                        </Badge>
                      </div>
                    )}
                  </div>

                  {semantic_analysis.common_indicators &&
                    semantic_analysis.common_indicators.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2">Common Indicators</h4>
                        <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                          {semantic_analysis.common_indicators.map((indicator, idx) => (
                            <li key={idx}>{indicator}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Suggested Rules */}
          {suggested_rules && suggested_rules.length > 0 && (
            <div className="border rounded-lg">
              <button
                onClick={() => toggleSection('rules')}
                className="w-full flex items-center justify-between p-4 hover:bg-accent transition-colors"
              >
                <h3 className="font-semibold text-lg">
                  Suggested Rules ({suggested_rules.length})
                </h3>
                {expandedSections.rules ? (
                  <ChevronUp className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </button>

              {expandedSections.rules && (
                <div className="p-4 border-t space-y-3">
                  {suggested_rules.map((rule, index) => (
                    <div
                      key={index}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedRuleIndex === index
                          ? 'bg-primary/10 border-primary'
                          : 'hover:bg-accent'
                      }`}
                      onClick={() => setSelectedRuleIndex(index)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {selectedRuleIndex === index && (
                              <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                            )}
                            <h4 className="font-medium">{rule.name}</h4>
                            {rule.coverage && (
                              <Badge variant="secondary">
                                {rule.coverage}% coverage
                              </Badge>
                            )}
                          </div>

                          <p className="text-sm text-muted-foreground mb-3">
                            {rule.description}
                          </p>

                          {rule.semantic_theme && (
                            <div className="flex items-start gap-2 mb-3 p-2 bg-blue-50 dark:bg-blue-950/20 rounded text-sm">
                              <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                              <span className="text-muted-foreground">
                                {rule.semantic_theme}
                              </span>
                            </div>
                          )}

                          {/* Conditions Preview */}
                          <div className="space-y-2">
                            <h5 className="text-xs font-medium text-muted-foreground uppercase">
                              Conditions ({rule.match_type === 'any' ? 'OR' : 'AND'})
                            </h5>
                            {rule.conditions.map((condition, condIndex) => (
                              <div
                                key={condIndex}
                                className="text-xs font-mono bg-muted p-2 rounded"
                              >
                                <span className="font-semibold">{condition.field}</span>{' '}
                                <span className="text-muted-foreground">
                                  {condition.operator}
                                </span>{' '}
                                <span className="text-primary">
                                  {Array.isArray(condition.value)
                                    ? `[${condition.value.slice(0, 3).join(', ')}${
                                        condition.value.length > 3
                                          ? `, +${condition.value.length - 3}`
                                          : ''
                                      }]`
                                    : condition.value}
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Actions Preview */}
                          <div className="mt-3">
                            <h5 className="text-xs font-medium text-muted-foreground uppercase mb-2">
                              Actions
                            </h5>
                            {rule.actions.map((action, actIndex) => (
                              <Badge key={actIndex} variant="outline" className="mr-2">
                                {action.type}
                                {(action.type === 'set_category' || action.type === 'set_priority') && action.value ? `: ${action.value}` : ''}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-muted/30">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>

          <Button
            onClick={handleCreateRule}
            disabled={
              !suggested_rules ||
              suggested_rules.length === 0 ||
              createRuleMutation.isPending
            }
          >
            {createRuleMutation.isPending ? 'Creating...' : 'Create Selected Rule'}
          </Button>
        </div>
      </div>
    </div>
  );
}
